import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'
import { formatOfficerDate } from '../../lib/officerFormat'

type Observation = {
  claim_id: string
  source_page: number | null
  entry_index: number | null
  reported_amount: string | null
  reported_currency: string | null
  value_date: string | null
  posting_date: string | null
  instrument_name: string | null
  recording: 'unlinked' | 'partially_recorded' | 'recorded' | 'needs_review'
}
type Readout = { observations: Observation[]; unlinked_count: number }

const labels: Record<Observation['recording'], string> = {
  unlinked: 'No trade link recorded',
  partially_recorded: 'Some recording receipts — review remaining cash',
  recorded: 'Trade link recorded',
  needs_review: 'Source observation needs review',
}

export default function PaymentObservationsCard({
  documentId,
  onPage,
}: {
  documentId: string
  onPage: (page: number) => void
}) {
  const session = useAuthSession()
  const key = `${documentId}:${session ?? ''}`
  const [result, setResult] = useState<{ key: string; value: Readout } | null>(null)
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const data = result?.key === key ? result.value : null
  const error = failedKey === key
  useEffect(() => {
    const controller = new AbortController()
    let active = true
    setResult(null)
    setFailedKey(null)
    apiFetch<Readout>(`/api/documents/${documentId}/payment-observations`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (
          !value ||
          !Array.isArray(value.observations) ||
          !Number.isInteger(value.unlinked_count)
        ) {
          throw new Error('Invalid payment observation response')
        }
        if (active) setResult({ key, value })
      })
      .catch(() => {
        if (active && !controller.signal.aborted) setFailedKey(key)
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [documentId, key])
  if (error)
    return (
      <section className="vcard" style={{ padding: 16 }} role="alert">
        Payment observations could not be loaded. Refresh this document to try again.
      </section>
    )
  if (!data?.observations.length) return null
  return (
    <section className="vcard" style={{ padding: 16 }} aria-label="Cash reported in this document">
      <h3 style={{ marginTop: 0 }}>Cash reported in this document</h3>
      <p style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
        Review the source and supporting trade documents. These observations do not create or change
        trades. Recorded links show past receipts.
      </p>
      {data.observations.map((row) => (
        <div key={row.claim_id} style={{ borderTop: '1px solid var(--line)', padding: '10px 0' }}>
          <div>
            <b>
              {row.reported_currency ?? ''} {row.reported_amount ?? 'Amount not stated'}
            </b>
          </div>
          <div style={{ fontSize: 'var(--text-12)' }}>
            Value date: {row.value_date ? formatOfficerDate(row.value_date) : 'Not stated'}
          </div>
          <div style={{ fontSize: 'var(--text-12)' }}>
            Instrument: {row.instrument_name ?? 'Not identified'}
          </div>
          <div
            style={{
              fontSize: 'var(--text-12)',
              color: row.recording === 'recorded' ? 'var(--muted)' : 'var(--gap)',
            }}
          >
            {labels[row.recording]}
          </div>
          {row.source_page != null && (
            <button type="button" className="vbtn" onClick={() => onPage(row.source_page!)}>
              View source page {row.source_page}
            </button>
          )}
        </div>
      ))}
    </section>
  )
}
